/**
 * Translation Context Provider
 * Provides real-time AI-powered translation for the entire application
 * Uses Gemini 3 Flash for instant translation when language changes
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

const TranslationContext = createContext();

// UI strings that need translation - organized by category
const UI_STRINGS = {
  // Common
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.add': 'Add',
  'common.search': 'Search',
  'common.filter': 'Filter',
  'common.submit': 'Submit',
  'common.loading': 'Loading...',
  'common.success': 'Success',
  'common.error': 'Error',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.next': 'Next',
  'common.previous': 'Previous',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.confirm': 'Confirm',
  'common.actions': 'Actions',
  'common.status': 'Status',
  'common.date': 'Date',
  'common.time': 'Time',
  'common.name': 'Name',
  'common.email': 'Email',
  'common.phone': 'Phone',
  'common.address': 'Address',
  'common.notes': 'Notes',
  'common.description': 'Description',
  'common.details': 'Details',
  'common.view': 'View',
  'common.download': 'Download',
  'common.upload': 'Upload',
  'common.print': 'Print',
  'common.export': 'Export',
  'common.import': 'Import',
  'common.refresh': 'Refresh',
  'common.reset': 'Reset',
  'common.clear': 'Clear',
  'common.select': 'Select',
  'common.selectAll': 'Select All',
  'common.none': 'None',
  'common.all': 'All',
  'common.active': 'Active',
  'common.inactive': 'Inactive',
  'common.pending': 'Pending',
  'common.completed': 'Completed',
  'common.approved': 'Approved',
  'common.rejected': 'Rejected',
  'common.new': 'New',
  'common.update': 'Update',
  'common.create': 'Create',
  'common.required': 'Required',
  'common.optional': 'Optional',
  
  // Navigation
  'nav.home': 'Home',
  'nav.dashboard': 'Dashboard',
  'nav.patients': 'Patients',
  'nav.scheduling': 'Scheduling',
  'nav.billing': 'Billing',
  'nav.reports': 'Reports',
  'nav.settings': 'Settings',
  'nav.logout': 'Logout',
  'nav.profile': 'Profile',
  'nav.help': 'Help',
  'nav.clinical': 'Clinical',
  'nav.admin': 'Admin',
  'nav.tools': 'Tools',
  
  // Patient
  'patient.firstName': 'First Name',
  'patient.lastName': 'Last Name',
  'patient.dateOfBirth': 'Date of Birth',
  'patient.gender': 'Gender',
  'patient.ssn': 'SSN',
  'patient.medicalRecordNumber': 'Medical Record Number',
  'patient.primaryDiagnosis': 'Primary Diagnosis',
  'patient.secondaryDiagnosis': 'Secondary Diagnosis',
  'patient.physician': 'Physician',
  'patient.insurance': 'Insurance',
  'patient.emergencyContact': 'Emergency Contact',
  'patient.admissionDate': 'Admission Date',
  'patient.dischargeDate': 'Discharge Date',
  'patient.caregiverName': 'Caregiver Name',
  'patient.allergies': 'Allergies',
  'patient.medications': 'Medications',
  
  // Clinical
  'clinical.vitalSigns': 'Vital Signs',
  'clinical.bloodPressure': 'Blood Pressure',
  'clinical.heartRate': 'Heart Rate',
  'clinical.temperature': 'Temperature',
  'clinical.respiratoryRate': 'Respiratory Rate',
  'clinical.oxygenSaturation': 'Oxygen Saturation',
  'clinical.weight': 'Weight',
  'clinical.height': 'Height',
  'clinical.painLevel': 'Pain Level',
  'clinical.assessment': 'Assessment',
  'clinical.plan': 'Plan',
  'clinical.interventions': 'Interventions',
  'clinical.goals': 'Goals',
  'clinical.signature': 'Signature',
  'clinical.dateSigned': 'Date Signed',
  
  // Forms
  'form.saveAsDraft': 'Save as Draft',
  'form.submitForm': 'Submit Form',
  'form.clearForm': 'Clear Form',
  'form.requiredField': 'This field is required',
  'form.invalidEmail': 'Invalid email address',
  'form.invalidPhone': 'Invalid phone number',
  'form.invalidDate': 'Invalid date',
  
  // Messages
  'message.saveSuccess': 'Saved successfully',
  'message.deleteSuccess': 'Deleted successfully',
  'message.updateSuccess': 'Updated successfully',
  'message.errorOccurred': 'An error occurred',
  'message.confirmDelete': 'Are you sure you want to delete this?',
  'message.noResults': 'No results found',
  'message.loadingData': 'Loading data...',
  
  // Dashboard
  'dashboard.welcome': 'Welcome',
  'dashboard.todayVisits': "Today's Visits",
  'dashboard.pendingTasks': 'Pending Tasks',
  'dashboard.upcomingAppointments': 'Upcoming Appointments',
  'dashboard.recentActivity': 'Recent Activity',
  'dashboard.quickActions': 'Quick Actions',
  
  // Scheduling
  'schedule.appointment': 'Appointment',
  'schedule.visitType': 'Visit Type',
  'schedule.duration': 'Duration',
  'schedule.recurring': 'Recurring',
  'schedule.assignedTo': 'Assigned To',
  
  // Billing
  'billing.invoice': 'Invoice',
  'billing.claim': 'Claim',
  'billing.payment': 'Payment',
  'billing.balance': 'Balance',
  'billing.dueDate': 'Due Date',
  'billing.amount': 'Amount',
  'billing.paid': 'Paid',
  'billing.unpaid': 'Unpaid',
  
  // Voice Dictation
  'voice.startDictation': 'Start Dictation',
  'voice.stopRecording': 'Stop Recording',
  'voice.listening': 'Listening...',
  'voice.clear': 'Clear',
  'voice.notSupported': 'Voice dictation is not supported in this browser',
  'voice.microphoneError': 'Microphone access denied',
  
  // Language
  'language.selectLanguage': 'Select Language',
  'language.translateTo': 'Translate to',
  'language.translating': 'Translating...',
  'language.convertToEnglish': 'Convert to English',
  'language.currentLanguage': 'Current Language'
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within TranslationProvider');
  }
  return context;
};

export const TranslationProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem('preferredLanguage') || 'en';
  });
  const [translations, setTranslations] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState(null);
  const translationCache = useRef({});
  const abortControllerRef = useRef(null);

  // Get translated string or fallback to English
  const t = useCallback((key, fallback) => {
    // If English, return original
    if (currentLanguage === 'en') {
      return fallback || UI_STRINGS[key] || key;
    }
    
    // Return cached translation or fallback
    return translations[key] || fallback || UI_STRINGS[key] || key;
  }, [currentLanguage, translations]);

  // Translate text dynamically (for user input, etc.)
  // First checks translation memory, then falls back to AI translation
  const translateText = useCallback(async (text, targetLang = currentLanguage, saveToMemory = true) => {
    if (!text || targetLang === 'en') return text;
    
    try {
      // First, check translation memory
      const memoryResponse = await api.post('/translate/memory/lookup', {
        texts: [text],
        target_language: targetLang
      });
      
      if (memoryResponse.data.success && memoryResponse.data.found.length > 0) {
        // Found in memory!
        return memoryResponse.data.found[0].translated;
      }
      
      // Not in memory, use AI translation
      const response = await api.post('/translate/translate', {
        text,
        target_language: targetLang
      });
      
      if (response.data.success) {
        // Save to memory for future use
        if (saveToMemory && response.data.translated) {
          try {
            await api.post('/translate/memory', {
              original_text: text,
              target_language: targetLang,
              translated_text: response.data.translated,
              category: 'general'
            });
          } catch (e) {
            // Silently fail memory save
          }
        }
        return response.data.translated;
      }
      return text;
    } catch (error) {
      console.error('Translation error:', error);
      return text;
    }
  }, [currentLanguage]);

  // Batch translate UI strings
  // First checks translation memory, then translates remaining via AI
  const translateUI = useCallback(async (targetLang) => {
    if (targetLang === 'en') {
      setTranslations({});
      setIsTranslating(false);
      return;
    }

    // Check cache first
    if (translationCache.current[targetLang]) {
      setTranslations(translationCache.current[targetLang]);
      setIsTranslating(false);
      return;
    }

    setIsTranslating(true);
    setTranslationError(null);

    // Cancel any ongoing translation
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const textsToTranslate = Object.values(UI_STRINGS);
      const keys = Object.keys(UI_STRINGS);
      const allTranslations = {};
      
      // First, check translation memory for all texts
      let textsNeedingTranslation = [...textsToTranslate];
      let keysNeedingTranslation = [...keys];
      
      try {
        const memoryResponse = await api.post('/translate/memory/lookup', {
          texts: textsToTranslate,
          target_language: targetLang
        });
        
        if (memoryResponse.data.success) {
          // Apply found translations
          memoryResponse.data.found.forEach(item => {
            const originalIndex = textsToTranslate.indexOf(item.original);
            if (originalIndex !== -1) {
              allTranslations[keys[originalIndex]] = item.translated;
            }
          });
          
          // Get texts not found in memory
          textsNeedingTranslation = memoryResponse.data.not_found;
          keysNeedingTranslation = textsNeedingTranslation.map(text => {
            const idx = textsToTranslate.indexOf(text);
            return keys[idx];
          });
        }
      } catch (memoryError) {
        console.log('Translation memory lookup failed, using AI for all');
      }
      
      // Translate remaining texts in batches
      if (textsNeedingTranslation.length > 0) {
        const batchSize = 50;
        
        for (let i = 0; i < textsNeedingTranslation.length; i += batchSize) {
          const batch = textsNeedingTranslation.slice(i, i + batchSize);
          const batchKeys = keysNeedingTranslation.slice(i, i + batchSize);
          
          const response = await api.post('/translate/translate-batch', {
            texts: batch,
            target_language: targetLang,
            source_language: 'en'
          });
          
          if (response.data.success) {
            response.data.translations.forEach((item, index) => {
              allTranslations[batchKeys[index]] = item.translated;
              
              // Save to translation memory for future use
              try {
                api.post('/translate/memory', {
                  original_text: item.original,
                  target_language: targetLang,
                  translated_text: item.translated,
                  category: 'ui_elements'
                });
              } catch (e) {
                // Silently fail
              }
            });
          }
        }
      }
      
      // Cache the translations
      translationCache.current[targetLang] = allTranslations;
      setTranslations(allTranslations);
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('UI Translation error:', error);
        setTranslationError('Translation failed. Some text may appear in English.');
      }
    } finally {
      setIsTranslating(false);
    }
  }, []);

  // Change language and trigger translation
  const changeLanguage = useCallback(async (langCode) => {
    setCurrentLanguage(langCode);
    localStorage.setItem('preferredLanguage', langCode);
    
    // Trigger UI translation
    await translateUI(langCode);
  }, [translateUI]);

  // Initialize translations on mount if not English
  useEffect(() => {
    if (currentLanguage !== 'en') {
      translateUI(currentLanguage);
    }
  }, []); // Only on mount

  // Get speech recognition language code
  const getSpeechLanguageCode = useCallback((langCode = currentLanguage) => {
    const speechCodes = {
      'en': 'en-US', 'es': 'es-ES', 'fr': 'fr-FR', 'zh': 'zh-CN',
      'ru': 'ru-RU', 'uk': 'uk-UA', 'vi': 'vi-VN', 'tl': 'fil-PH',
      'hi': 'hi-IN', 'ur': 'ur-PK', 'ja': 'ja-JP', 'ko': 'ko-KR', 'th': 'th-TH',
      'fa': 'fa-IR', 'ar': 'ar-SA', 'he': 'he-IL', 'ga': 'ga-IE',
      'de': 'de-DE', 'pt': 'pt-BR', 'it': 'it-IT', 'pl': 'pl-PL', 'nl': 'nl-NL',
      'af': 'af-ZA', 'sw': 'sw-KE', 'am': 'am-ET', 'ha': 'ha-NG',
      'ig': 'ig-NG', 'yo': 'yo-NG', 'zu': 'zu-ZA', 'xh': 'xh-ZA', 'so': 'so-SO'
    };
    return speechCodes[langCode] || langCode;
  }, [currentLanguage]);

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    translateText,
    isTranslating,
    translationError,
    getSpeechLanguageCode,
    UI_STRINGS
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export default TranslationContext;
